/**
 * data/fleet.ts — the Car Hire side of Grace Haven.
 *
 * These are only the DEFAULT vehicles. The live fleet is edited from the
 * portal's Fleet page (Firestore `fleet/list`) and read via `useFleet()`,
 * which falls back to this seed so the public site always renders.
 */
export interface Vehicle {
  id: string;
  name: string;
  category: string;     // e.g. "Compact", "Sedan", "SUV", "Luxury"
  pricePerDay: number;  // BWP per day
  seats?: number;
  transmission?: string; // "Automatic" | "Manual"
  fuel?: string;         // "Petrol" | "Diesel" | "Hybrid"
  blurb?: string;
  features?: string[];
  image?: string;        // direct image URL (edited in the portal)
  active?: boolean;      // default true; set false to hide without deleting
}

export const FLEET_DEFAULTS: Vehicle[] = [
  {
    id: 'compact',
    name: 'Compact Hatchback',
    category: 'Compact',
    pricePerDay: 350,
    seats: 5,
    transmission: 'Manual',
    fuel: 'Petrol',
    blurb: 'Nippy and economical — perfect for getting around Gaborone.',
    features: ['5 seats', 'Air-con', 'Fuel-efficient', 'Bluetooth'],
  },
  {
    id: 'sedan',
    name: 'Executive Sedan',
    category: 'Sedan',
    pricePerDay: 550,
    seats: 5,
    transmission: 'Automatic',
    fuel: 'Petrol',
    blurb: 'Comfortable and refined for business trips or airport runs.',
    features: ['5 seats', 'Automatic', 'Air-con', 'Cruise control'],
  },
  {
    id: 'suv',
    name: '4x4 SUV',
    category: 'SUV',
    pricePerDay: 850,
    seats: 7,
    transmission: 'Automatic',
    fuel: 'Diesel',
    blurb: 'Spacious and capable — ready for the family and the open road.',
    features: ['7 seats', '4x4', 'Automatic', 'Roof rack'],
  },
];

/** Typical car-hire terms. Edit to match the business's rules. */
export const HIRE_POLICY = {
  minAge: '23 years',
  conditions: [
    'Rates are per vehicle, per day, in Botswana Pula (BWP).',
    'A valid driver’s licence (held 2+ years) and ID/passport are required.',
    'Minimum driver age is 23.',
    'A refundable security deposit is payable at collection.',
    'Fuel is charged on a full-to-full basis — return with a full tank.',
    'Comprehensive insurance is included; an excess applies to any claim.',
    'Unlimited mileage within Botswana; cross-border travel by prior arrangement.',
    'Free cancellation up to 48 hours before collection.',
  ],
} as const;
